import type { Band, RangeSpec, Sex } from './template';
import type { ParsedValue } from './values';
import { normalizeQualitative } from './values';

export interface PatientContext {
  sex?: Sex;
  ageDays?: number;
}

export type FlagCode = 'L' | 'H' | 'LL' | 'HH' | 'A';

export interface Flag {
  code: FlagCode | null;
  /** Band label when the range is banded, e.g. "Insufficiency". */
  band?: string;
  critical: boolean;
}

export const NO_FLAG: Flag = { code: null, critical: false };

/**
 * Pick the most specific range that applies to the patient: sex + age beats sex beats age
 * beats generic. Returns null when only ranges for another sex exist, or when sex-specific
 * ranges exist but the patient's sex is unknown (we never guess).
 */
export function selectRange(
  ranges: readonly RangeSpec[],
  patient: PatientContext,
): RangeSpec | null {
  let best: RangeSpec | null = null;
  let bestScore = -1;
  for (const r of ranges) {
    if (r.sex !== undefined && r.sex !== patient.sex) continue;
    const hasAge = r.ageMinDays !== undefined || r.ageMaxDays !== undefined;
    if (hasAge) {
      if (patient.ageDays === undefined) continue;
      if (r.ageMinDays !== undefined && patient.ageDays < r.ageMinDays) continue;
      if (r.ageMaxDays !== undefined && patient.ageDays > r.ageMaxDays) continue;
    }
    const score = (r.sex !== undefined ? 2 : 0) + (hasAge ? 1 : 0);
    if (score > bestScore) {
      best = r;
      bestScore = score;
    }
  }
  return best;
}

function inBand(b: Band, v: number): boolean {
  if (b.min !== undefined && (b.minExclusive ? v <= b.min : v < b.min)) return false;
  if (b.max !== undefined && (b.maxInclusive ? v > b.max : v >= b.max)) return false;
  return true;
}

function bandFlag(b: Band): FlagCode | null {
  if (b.flag === 'low') return 'L';
  if (b.flag === 'high') return 'H';
  if (b.flag === 'abnormal') return 'A';
  return null;
}

/** Bounds the range accepts; undefined means unbounded on that side. */
function bounds(range: RangeSpec): {
  min?: number;
  minIncl: boolean;
  max?: number;
  maxIncl: boolean;
} {
  switch (range.type) {
    case 'between':
      return { min: range.min, minIncl: true, max: range.max, maxIncl: true };
    case 'lt':
      return { max: range.max, maxIncl: false, minIncl: true };
    case 'lte':
      return { max: range.max, maxIncl: true, minIncl: true };
    case 'gt':
      return { min: range.min, minIncl: false, maxIncl: true };
    case 'gte':
      return { min: range.min, minIncl: true, maxIncl: true };
    default:
      return { minIncl: true, maxIncl: true };
  }
}

function belowMin(v: number, b: ReturnType<typeof bounds>): boolean {
  return b.min !== undefined && (b.minIncl ? v < b.min : v <= b.min);
}
function aboveMax(v: number, b: ReturnType<typeof bounds>): boolean {
  return b.max !== undefined && (b.maxIncl ? v > b.max : v >= b.max);
}

/**
 * Flag a value against a range. Callers pass the value as printed (already rounded), so the
 * flag always agrees with what the reader sees on paper.
 */
export function evaluate(
  value: ParsedValue,
  range: RangeSpec | null,
  critical?: { low?: number | undefined; high?: number | undefined },
): Flag {
  if (value.kind === 'empty' || range === null) return NO_FLAG;

  if (range.type === 'text') return NO_FLAG;

  if (range.type === 'qualitative') {
    const text =
      value.kind === 'text'
        ? value.text
        : value.kind === 'number'
          ? String(value.value)
          : value.kind === 'span'
            ? `${value.low}-${value.high}`
            : `${value.comparator}${value.value}`;
    const ok = range.expected.some((e) => normalizeQualitative(e) === normalizeQualitative(text));
    return ok ? NO_FLAG : { code: 'A', critical: false };
  }

  if (value.kind === 'text') return NO_FLAG;

  if (range.type === 'bands') {
    const v =
      value.kind === 'number' || value.kind === 'censored'
        ? value.value
        : value.kind === 'span'
          ? value.high
          : null;
    if (v === null) return NO_FLAG;
    const band = range.bands.find((b) => inBand(b, v));
    if (!band) return NO_FLAG;
    return { code: bandFlag(band), band: band.label, critical: isCritical(v, v, critical) };
  }

  const b = bounds(range);
  let low = false;
  let high = false;
  let lo: number;
  let hi: number;

  if (value.kind === 'number') {
    lo = hi = value.value;
    low = belowMin(lo, b);
    high = aboveMax(hi, b);
  } else if (value.kind === 'span') {
    lo = value.low;
    hi = value.high;
    low = belowMin(lo, b);
    high = aboveMax(hi, b);
  } else {
    // Censored: flag only when the true value is certainly outside the range.
    lo = hi = value.value;
    if (value.comparator === '<' || value.comparator === '<=') {
      low = b.min !== undefined && value.value <= b.min;
      lo = Number.NEGATIVE_INFINITY;
    } else {
      high = b.max !== undefined && value.value >= b.max;
      hi = Number.POSITIVE_INFINITY;
    }
  }

  const crit = isCritical(lo, hi, critical);
  if (high) return { code: crit ? 'HH' : 'H', critical: crit };
  if (low) return { code: crit ? 'LL' : 'L', critical: crit };
  return { code: null, critical: crit };
}

function isCritical(
  lo: number,
  hi: number,
  critical?: { low?: number | undefined; high?: number | undefined },
): boolean {
  if (!critical) return false;
  return (
    (critical.low !== undefined && lo <= critical.low) ||
    (critical.high !== undefined && hi >= critical.high)
  );
}

/** Printed reference text for a range, e.g. "13.0 - 17.0", "< 40", band lines. */
export function describeRange(range: RangeSpec): string {
  if (range.display) return range.display;
  switch (range.type) {
    case 'between':
      return `${range.min} - ${range.max}`;
    case 'lt':
      return `< ${range.max}`;
    case 'lte':
      return `≤ ${range.max}`;
    case 'gt':
      return `> ${range.min}`;
    case 'gte':
      return `≥ ${range.min}`;
    case 'bands':
      return range.bands.map((b) => `${b.label}: ${describeBand(b)}`).join('\n');
    case 'qualitative':
      return range.expected[0] ?? '';
    case 'text':
      return '';
  }
}

function describeBand(b: Band): string {
  if (b.min !== undefined && b.max !== undefined) return `${b.min} - ${b.max}`;
  if (b.max !== undefined) return `${b.maxInclusive ? '≤' : '<'} ${b.max}`;
  if (b.min !== undefined) return `${b.minExclusive ? '>' : '≥'} ${b.min}`;
  return '';
}

const NUM = String.raw`[+-]?\d+(?:\.\d+)?`;

/**
 * Turn what a lab types in a reference cell into a range the software can flag against.
 * "13 - 17", "13.0-17.0" → between; "<40", "< 40" → lt; "≤ 20", "<= 20", "upto 20" → lte;
 * ">60", "More than 60 %" → gt; "≥ 40" → gte; "Negative"/"Nil" → qualitative;
 * anything else is kept as printed text and not flagged.
 * The original text is kept as `display` whenever it differs from the generated form, so the
 * report prints exactly what the lab typed.
 */
export function parseRangeText(input: string): RangeSpec | null {
  const text = input.trim();
  if (!text) return null;
  const t = text.toLowerCase().replace(/,/g, '').replace(/\s+/g, ' ');
  const withDisplay = (r: RangeSpec): RangeSpec =>
    describeRange(r) === text ? r : ({ ...r, display: text } as RangeSpec);

  const between = new RegExp(`^(${NUM})\\s*(?:-|–|to)\\s*(${NUM})(?:\\s*\\S+)?$`).exec(t);
  if (between) {
    const min = Number(between[1]);
    const max = Number(between[2]);
    if (min <= max) return withDisplay({ type: 'between', min, max });
  }
  const one = new RegExp(
    `^(<=|>=|≤|≥|<|>|less than|more than|greater than|below|above|up ?to|upto|max|min)\\s*(${NUM})(?:\\s*\\S+)?$`,
  ).exec(t);
  if (one) {
    const op = one[1] as string;
    const n = Number(one[2]);
    if (op === '<' || op === 'less than' || op === 'below')
      return withDisplay({ type: 'lt', max: n });
    if (op === '<=' || op === '≤' || /^up ?to$/.test(op) || op === 'max')
      return withDisplay({ type: 'lte', max: n });
    if (op === '>' || op === 'more than' || op === 'greater than' || op === 'above')
      return withDisplay({ type: 'gt', min: n });
    return withDisplay({ type: 'gte', min: n });
  }
  if (/^(nil|negative|absent|not detected|non[- ]?reactive|clear|normal)$/.test(t)) {
    return { type: 'qualitative', expected: [text] };
  }
  return { type: 'text', display: text };
}

/** Split form for templates that print separate Min and Max columns. */
export function rangeMinMax(range: RangeSpec): { min: string; max: string } {
  switch (range.type) {
    case 'between':
      return { min: String(range.min), max: String(range.max) };
    case 'lt':
    case 'lte':
      return { min: '', max: describeRange(range) };
    case 'gt':
    case 'gte':
      return { min: describeRange(range), max: '' };
    default:
      return { min: describeRange(range), max: '' };
  }
}
